"use client";

import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { usePermisos } from "./usePermisos";
import { type Role } from "@/lib/roles";
import { getSessionMirror } from "@/lib/session-mirror";
import { isOnline } from "@/lib/hooks/useNetworkStatus";
import { esChoferPorPermisosArray } from "@/lib/auth-chofer";

/**
 * Hook canónico de acceso del cliente.
 *
 * Fase 4 (2026-05-20): toda decisión de acceso sale del set de perfiles
 * resuelto por `/api/usuarios/permisos`. Eliminados los fallbacks legacy a
 * `canAccessRoute(role)` y `tienePermiso(role, p)`. Las únicas dispensas
 * que permanecen son por UX:
 *   - `loading=true` → permisivo (pre-mount, mientras se carga el fetch).
 *   - offline sin info → permisivo (el server enforza al reconectar).
 *
 * El campo `role` se conserva solo para display (badges, "viendo como").
 * Para autorizar acción, usar `isAdmin`, `canAccess(path)` o `puede(permiso)`.
 */
export function useRole() {
  const { data: session, status } = useSession();
  const permisos = usePermisos();
  const loading = status === "loading" || permisos.loading;
  // Display role: prefer session.user.role; si NextAuth dropped (offline),
  // caer al espejo de sesión. Se usa solo para mostrar etiquetas.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sessionRole = ((session?.user as any)?.role as Role | undefined) ?? undefined;
  const mirroredRole = (() => {
    if (sessionRole) return undefined;
    const m = getSessionMirror();
    return (m?.role as Role | undefined) ?? undefined;
  })();
  const realRole: Role = sessionRole ?? mirroredRole ?? "user";

  // Impersonate: leído de sessionStorage. role visible solo para display.
  const [impersonateRole, setImpersonateRole] = useState<Role | null>(null);
  useEffect(() => {
    try {
      const raw = typeof window !== "undefined" ? sessionStorage.getItem("cacique_impersonate") : null;
      if (!raw) {
        setImpersonateRole(null);
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed?.role) {
        setImpersonateRole(parsed.role as Role);
        return;
      }
      if (parsed?.userId) {
        fetch(`/api/admin/usuarios`)
          .then((r) => (r.ok ? r.json() : []))
          .then((users) => {
            const u = (Array.isArray(users) ? users : []).find(
              (u: { id: string }) => u.id === parsed.userId,
            );
            if (u?.role) setImpersonateRole(u.role as Role);
          })
          .catch(() => {});
      }
    } catch {
      /* ignore */
    }
  }, []);

  const isImpersonating = impersonateRole !== null;
  const role = isImpersonating ? impersonateRole : realRole;

  // Detectar dinámicamente acceso al portal /chofer (sin hardcode de
  // nombre de perfil). True si el usuario tiene CUALQUIER permiso del
  // grupo chofer.* / firma.* via sus perfiles, O role legacy "chofer".
  // Single source of truth en lib/auth-chofer.ts — mismo helper que usa
  // el server (requireChofer + endpoints admin).
  const esChofer = (() => {
    if (realRole === "chofer") return true;
    if (esChoferPorPermisosArray(permisos.permisos)) return true;
    return false;
  })();

  return {
    role,
    isAdmin: permisos.esAdmin,
    esChofer,
    loading,
    canAccess: (path: string) => {
      // Admin pasa todo.
      if (permisos.esAdmin) return true;
      // UX: durante el primer fetch o offline, ser permisivo. El server
      // (middleware + guards) son la fuente de verdad final.
      if (loading) return true;
      if (typeof window !== "undefined" && !isOnline()) return true;
      // Decisión final: secciones del perfil.
      return permisos.puedeVer(path);
    },
    puede: (p: string) => {
      if (permisos.esAdmin) return true;
      return permisos.puede(p);
    },
    puedeEscribir: permisos.puedeEscribir,
    perfiles: permisos.perfiles,
    permisos: permisos.permisos,
  };
}
